import { APP_VERSION, type RoomSessionResponse } from "@secret-cabinet/contracts";
import { ArrowLeft, ArrowRight, MonitorUp } from "lucide-react";
import { useRef, useState, type FormEvent } from "react";
import { ApiError, createTvRoom } from "./api";

interface LandingProps {
  onRoomCreated: (response: RoomSessionResponse) => void;
  onJoinCode: (code: string) => void;
}

const gameplaySlides = [
  { src: "/gameplay/election.png", title: "An election begins", eyebrow: "01 / PUBLIC CHAMBER", text: "The shared TV keeps every player, office, policy track, and ballot count in view." },
  { src: "/gameplay/policy.png", title: "The table reacts", eyebrow: "02 / PUBLIC RESULT", text: "A policy changes the room. Private hands and roles stay on each player's device." },
  { src: "/gameplay/victory.png", title: "A final verdict", eyebrow: "03 / GAME COMPLETE", text: "The chamber announces the result after the last decisive vote or policy." },
] as const;

export function Landing({ onRoomCreated, onJoinCode }: LandingProps) {
  const [code, setCode] = useState("");
  const [mode, setMode] = useState<"create" | "join">("create");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [slide, setSlide] = useState(0);
  const touchStart = useRef<number | null>(null);
  const activeSlide = gameplaySlides[slide] ?? gameplaySlides[0]!;
  const showSlide = (next: number) => setSlide((next + gameplaySlides.length) % gameplaySlides.length);

  async function createRoom(): Promise<void> {
    setCreating(true);
    setError(null);
    try {
      onRoomCreated(await createTvRoom());
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "The chamber could not be opened.");
      setCreating(false);
    }
  }

  function join(event: FormEvent): void {
    event.preventDefault();
    const normalized = code.trim().toUpperCase();
    if (!/^[ABCDEFGHJKLMNPQRSTUVWXYZ]{4}$/.test(normalized)) {
      setError("Enter the four letters shown on the TV.");
      return;
    }
    onJoinCode(normalized);
  }

  return (
    <div className="home-shell">
      <header className="home-topbar">
        <div className="home-wordmark"><span className="home-wordmark__mark">SC</span><span>Secret Cabinet</span></div>
        <span className="home-version">v{APP_VERSION}</span>
      </header>
      <main className="home-layout">
        <section className="home-hero" aria-labelledby="landing-title">
          <span className="eyebrow">A GAME OF HIDDEN LOYALTIES</span>
          <h1 id="landing-title">Trust less.<br /><em>Vote wisely.</em></h1>
          <p>The TV runs the chamber. Five to ten players join on separate devices to debate, vote, and keep their roles private.</p>
          <div className="game-facts" aria-label="Secret Cabinet game requirements"><span>5–10 PLAYERS</span><span>TV REQUIRED</span><span>PVP</span></div>
        </section>

        <section className="entry-panel" aria-label="Start or join a game">
          <div className="entry-tabs" role="tablist" aria-label="Room entry">
            <button className={mode === "create" ? "is-active" : ""} type="button" role="tab" aria-selected={mode === "create"} onClick={() => { setMode("create"); setError(null); }}>Open TV room</button>
            <button className={mode === "join" ? "is-active" : ""} type="button" role="tab" aria-selected={mode === "join"} onClick={() => { setMode("join"); setError(null); }}>Join game</button>
          </div>
          {mode === "create" ? (
            <div className="entry-form entry-form--tv" role="tabpanel">
              <span className="entry-kicker">THE SHARED CHAMBER</span>
              <div className="tv-glyph"><MonitorUp aria-hidden="true" /></div>
              <h2>Open the room on your TV.</h2>
              <p>The TV displays the public board and join code. It never takes a seat or sees a secret role.</p>
              <button className="entry-submit" type="button" onClick={() => void createRoom()} disabled={creating}><span>{creating ? "Opening chamber…" : "Create TV room"}</span><ArrowRight aria-hidden="true" className="icon" /></button>
              <small>Keep this screen open while five to ten players join on their own devices.</small>
            </div>
          ) : (
            <form className="entry-form entry-form--join" role="tabpanel" onSubmit={join}>
              <span className="entry-kicker">YOUR PRIVATE SEAT</span>
              <h2>Join on your device.</h2>
              <p>Enter the four-letter code shown on the required TV.</p>
              <label htmlFor="room-code">Room code</label>
              <input id="room-code" className="room-input" value={code} onChange={(event) => setCode(event.target.value.toUpperCase().replace(/[^A-HJ-NP-Z]/g, "").slice(0, 4))} autoCapitalize="characters" autoComplete="off" spellCheck={false} inputMode="text" maxLength={4} placeholder="ABCD" />
              <button className="entry-submit" type="submit" disabled={code.length !== 4}><span>Continue to game</span><ArrowRight aria-hidden="true" className="icon" /></button>
              <small>Private roles, ballots, and decisions stay on this device.</small>
            </form>
          )}
          {error ? <p className="entry-error" role="alert">{error}</p> : null}
        </section>

        <section className="gameplay-showcase" aria-labelledby="gameplay-showcase-title">
          <div className="showcase-heading"><div><p className="eyebrow">SEE IT IN PLAY</p><h2 id="gameplay-showcase-title">From first vote to victory.</h2></div><p>Real public chamber screens. Every hidden decision remains private on a player's device.</p></div>
          <div className="gameplay-slider" role="region" aria-roledescription="carousel" aria-label="In-game screenshots" tabIndex={0} onKeyDown={(event) => { if (event.key === "ArrowLeft") showSlide(slide - 1); if (event.key === "ArrowRight") showSlide(slide + 1); }} onTouchStart={(event) => { touchStart.current = event.changedTouches[0]?.clientX ?? null; }} onTouchEnd={(event) => { if (touchStart.current === null) return; const distance = (event.changedTouches[0]?.clientX ?? touchStart.current) - touchStart.current; if (Math.abs(distance) > 50) showSlide(slide + (distance < 0 ? 1 : -1)); touchStart.current = null; }}>
            <div className="gameplay-frame"><img src={activeSlide.src} alt={`${activeSlide.title} Secret Cabinet public chamber screen.`} width="1920" height="1080" loading="lazy" /></div>
            <div className="gameplay-caption" aria-live="polite"><p className="eyebrow">{activeSlide.eyebrow}</p><h3>{activeSlide.title}</h3><p>{activeSlide.text}</p></div>
            <div className="slider-controls"><button type="button" onClick={() => showSlide(slide - 1)} aria-label="Previous screenshot"><ArrowLeft aria-hidden="true" className="icon" /></button><div>{gameplaySlides.map((item, index) => <button key={item.src} type="button" className={index === slide ? "active" : ""} onClick={() => showSlide(index)} aria-label={`Show screenshot ${index + 1}: ${item.title}`} aria-current={index === slide ? "true" : undefined} />)}</div><button type="button" onClick={() => showSlide(slide + 1)} aria-label="Next screenshot"><ArrowRight aria-hidden="true" className="icon" /></button></div>
          </div>
        </section>
      </main>
      <footer className="home-footer"><a href="https://games.xile.se">All Xile games <ArrowRight aria-hidden="true" className="icon" /></a><span>Noncommercial adaptation of Secret Hitler under CC BY-NC-SA 4.0 · design by Max Temkin, Mike Boxleiter, and Tommy Maranges</span></footer>
    </div>
  );
}
